import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import BBALoader from '../components/BBALoader';
import { FaBook, FaPlus, FaArrowUp, FaArrowDown, FaPencilAlt } from 'react-icons/fa';
import { X } from 'lucide-react';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import SaveButton from '../components/SaveButton';
import EditButton from '../components/EditButton';
import SopRichEditor from '../components/sop/SopRichEditor';
import SopRichView from '../components/sop/SopRichView';
import { migrateSopRecord, flattenStepContent } from '../utils/sopContent';
import { suggestNextVersion } from '../services/hrCollectionsService';
import { fetchGroupEmojis, saveGroupEmoji, defaultGroupEmojis } from '../services/groupEmojiService';
import ContactToBuildTemplateEditor from '../components/sop/ContactToBuildTemplateEditor';
import SopHtmlTemplateEditor from '../components/sop/SopHtmlTemplateEditor';
import PortalBackground from '../components/PortalBackground';

// Same full emoji picker used by the Sales / HR material templates.
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

const SOPTemplates = () => {
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('design');
  const [selectedSOP, setSelectedSOP] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSOP, setEditedSOP] = useState(null);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [isAttachmentOnly, setIsAttachmentOnly] = useState(false);
  const [newTemplateAttachmentFile, setNewTemplateAttachmentFile] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPickerMode, setEmojiPickerMode] = useState('card'); // 'card' | 'group'
  const [groupEmojis, setGroupEmojis] = useState(defaultGroupEmojis('sop'));
  const [sops, setSops] = useState({
    design: [],
    construction: [],
    office: []
  });
  // Footer container node for the editor's formatting toolbar (portaled in).
  const [toolbarSlot, setToolbarSlot] = useState(null);

  useEffect(() => {
    fetchGroupEmojis('sop').then(setGroupEmojis).catch(() => {});
  }, []);

  const tabs = [
    { id: 'design', label: 'Design' },
    { id: 'construction', label: 'Construction' },
    { id: 'office', label: 'Office' },
    { id: 'contact-to-build', label: 'Contact to Build' },
    // staticEmoji: not a portal group — fixed emoji, no group-emoji pencil.
    { id: 'html-template', label: 'HTML Template', staticEmoji: '🎨' }
  ];

  // Fetch SOPs from Firestore
  useEffect(() => {
    fetchSOPs();
  }, []);

  const fetchSOPs = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'sopTemplates'));
      const sopsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Group SOPs by department, ordered by the saved `order` field. Sort is
      // stable, so templates without an order keep their existing sequence.
      const byOrder = (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
      const groupedSOPs = {
        design: sopsData.filter(sop => sop.department === 'design').sort(byOrder),
        construction: sopsData.filter(sop => sop.department === 'construction').sort(byOrder),
        office: sopsData.filter(sop => sop.department === 'office').sort(byOrder)
      };

      setSops(groupedSOPs);
    } catch (error) {
      console.error('Error fetching SOPs:', error);
    }
  };

  // Reorder a template up/down within its department. Optimistic swap, then
  // persist `order` on every doc in the department (index = display order).
  // This order carries through to the client portal SOPs page.
  const moveSOP = async (index, direction) => {
    const list = sops[activeTab] || [];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;

    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    setSops(prev => ({ ...prev, [activeTab]: next }));

    try {
      const batch = writeBatch(db);
      next.forEach((sop, i) => {
        if (sop.id && typeof sop.id === 'string') {
          batch.update(doc(db, 'sopTemplates', sop.id), { order: i, updatedAt: serverTimestamp() });
        }
      });
      await batch.commit();
      // Reflect the persisted order numbers locally so further moves are stable.
      setSops(prev => ({
        ...prev,
        [activeTab]: (prev[activeTab] || []).map((s, i) => ({ ...s, order: i }))
      }));
    } catch (error) {
      console.error('Error reordering SOPs:', error);
      fetchSOPs();
    }
  };

  const handleSOPClick = (sop) => {
    // Ensure the SOP has all required properties (include attachmentOnly and attachment for attachment-only templates)
    const safeSOP = {
      id: sop.id || null,
      title: sop.title || '',
      emoji: sop.emoji || '',
      version: sop.version || '1.0',
      description: sop.description || '',
      department: sop.department || 'design',
      steps: sop.steps || [],
      // Prefer saved Tiptap JSON; fall back to a live conversion of the legacy
      // fields so pre-migration templates open with their content intact.
      // Flatten so older step-format docs open as the plain document shape.
      contentJSON: flattenStepContent(sop.contentJSON || migrateSopRecord(sop)),
      attachmentOnly: sop.attachmentOnly || false,
      attachment: sop.attachment || null
    };
    
    setSelectedSOP(safeSOP);
    setEditedSOP({ ...safeSOP });
    setIsModalOpen(true);
  };

  // CRM sidebar search deep-links land here as ?open=<sopId> — switch to the
  // SOP's department tab and open its document modal once templates load.
  const [searchParams] = useSearchParams();
  const pendingOpenRef = useRef(searchParams.get('open'));
  useEffect(() => {
    const openId = pendingOpenRef.current;
    if (!openId) return;
    const target = [...(sops.design || []), ...(sops.construction || []), ...(sops.office || [])].find(
      (s) => s.id === openId
    );
    if (!target) return;
    pendingOpenRef.current = null;
    setActiveTab(target.department || 'design');
    handleSOPClick(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sops]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  // Bump the template version (1.0 → 1.1 → … → 1.9 → 2.0). Persisted on Save.
  const bumpVersion = () =>
    setEditedSOP((prev) => ({ ...(prev || {}), version: suggestNextVersion(prev?.version || '1.0') }));

  const handleNewTemplate = () => {
    const newTemplate = {
      title: 'New Template',
      emoji: '',
      version: '1.0',
      description: 'Template description',
      department: activeTab || 'design',
      steps: []
    };
    
    // Ensure the department exists in the sops state
    if (!sops[newTemplate.department]) {
      setSops(prev => ({
        ...prev,
        [newTemplate.department]: []
      }));
    }
    
    setSelectedSOP(newTemplate);
    setEditedSOP(newTemplate);
    setIsNewTemplate(true);
    setIsAttachmentOnly(false);
    setNewTemplateAttachmentFile(null);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDeleteSOP = async () => {
    if (window.confirm('Are you sure you want to delete this SOP template?')) {
      try {
        // Only delete from Firestore if it has a valid ID
        if (selectedSOP.id && typeof selectedSOP.id === 'string') {
          await deleteDoc(doc(db, 'sopTemplates', selectedSOP.id));
        }
        
        // Update local state
        setSops(prev => {
          const currentDeptArray = prev[activeTab] || [];
          return {
            ...prev,
            [activeTab]: currentDeptArray.filter(s => {
              // If the SOP being deleted has no ID, remove it by comparing other properties
              if (!selectedSOP.id) {
                return s.title !== selectedSOP.title || s.description !== selectedSOP.description;
              }
              return s.id !== selectedSOP.id;
            })
          };
        });
        
        // Close modal
        setIsModalOpen(false);
        setIsEditing(false);
        setSelectedSOP(null);
        setEditedSOP(null);
      } catch (error) {
        console.error('Error deleting SOP:', error);
      }
    }
  };

  const handleSave = async () => {
    try {
      // Validate data before saving
      if (!editedSOP || !editedSOP.department) {
        console.error('Invalid SOP data:', editedSOP);
        return;
      }

      if (isNewTemplate) {
        // Add new template to Firestore
        const isAttachmentOnlyTemplate = isAttachmentOnly;
        if (isAttachmentOnlyTemplate && !newTemplateAttachmentFile) {
          alert('Please select an attachment file before saving.');
          return;
        }
        const sopData = {
          title: editedSOP.title || '',
          emoji: editedSOP.emoji || '',
          version: editedSOP.version || '1.0',
          description: editedSOP.description || '',
          department: editedSOP.department,
          steps: editedSOP.steps || [],
          // Tiptap JSON is the primary content; legacy steps[] stays as fallback.
          contentJSON: editedSOP.contentJSON || null,
          order: (sops[editedSOP.department] || []).length,
          attachmentOnly: isAttachmentOnlyTemplate,
          attachment: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        const docRef = await addDoc(collection(db, 'sopTemplates'), sopData);

        // If attachment-only, upload file and update doc with attachment URL
        if (isAttachmentOnlyTemplate && newTemplateAttachmentFile) {
          try {
            const path = `sopTemplates/${docRef.id}/attachment/${Date.now()}-${newTemplateAttachmentFile.name}`;
            const fileRef = storageRef(storage, path);
            await uploadBytes(fileRef, newTemplateAttachmentFile);
            const url = await getDownloadURL(fileRef);
            const attachmentData = { name: newTemplateAttachmentFile.name, url };
            await updateDoc(doc(db, 'sopTemplates', docRef.id), {
              attachment: attachmentData,
              updatedAt: serverTimestamp()
            });
            sopData.attachment = attachmentData;
          } catch (uploadError) {
            console.error('Attachment upload failed:', uploadError);
            // Remove the doc we just created so we don't leave a template without attachment
            await deleteDoc(doc(db, 'sopTemplates', docRef.id));
            alert('Failed to upload attachment. Please check your connection and try again.');
            return;
          }
        }

        // Update local state with the new SOP
        const newSOPWithId = { id: docRef.id, ...sopData };
        setSops(prev => {
          const currentDeptArray = prev[editedSOP.department] || [];
          return {
            ...prev,
            [editedSOP.department]: [...currentDeptArray, newSOPWithId]
          };
        });

        setSelectedSOP(newSOPWithId);
        setEditedSOP(newSOPWithId);
        setNewTemplateAttachmentFile(null);
      } else {
        // Update existing template in Firestore
        if (!selectedSOP.id || typeof selectedSOP.id !== 'string') {
          console.error('Invalid SOP ID for update:', selectedSOP.id);
          return;
        }
        
        const docRef = doc(db, 'sopTemplates', selectedSOP.id);
        await updateDoc(docRef, {
          title: editedSOP.title || '',
          emoji: editedSOP.emoji || '',
          version: editedSOP.version || '1.0',
          description: editedSOP.description || '',
          department: editedSOP.department,
          steps: editedSOP.steps || [],
          // Additive: write Tiptap JSON, leave legacy steps[] as the fallback.
          contentJSON: editedSOP.contentJSON || null,
          updatedAt: serverTimestamp()
        });
        
        // Update local state - handle department change
        setSops(prev => {
          const newSops = { ...prev };
          
          // Ensure both departments exist as arrays
          if (!newSops[selectedSOP.department]) {
            newSops[selectedSOP.department] = [];
          }
          if (!newSops[editedSOP.department]) {
            newSops[editedSOP.department] = [];
          }
          
          // Remove from old department if department changed
          if (selectedSOP.department !== editedSOP.department) {
            newSops[selectedSOP.department] = (prev[selectedSOP.department] || []).filter(sop => sop.id !== selectedSOP.id);
          }
          
          // Add to new department or update in current department
          if (selectedSOP.department !== editedSOP.department) {
            // Department changed - add to new department
            const updatedSOP = { ...editedSOP, id: selectedSOP.id };
            newSops[editedSOP.department] = [...(prev[editedSOP.department] || []), updatedSOP];
          } else {
            // Same department - update existing
            newSops[editedSOP.department] = (prev[editedSOP.department] || []).map(sop => 
              sop.id === selectedSOP.id ? { ...editedSOP, id: selectedSOP.id } : sop
            );
          }
          
          return newSops;
        });
        
        // Update the selected SOP with edited data
        setSelectedSOP({ ...editedSOP, id: selectedSOP.id });
      }
      
      setIsEditing(false);
      setIsNewTemplate(false);
    } catch (error) {
      console.error('Error saving SOP:', error);
      console.error('Error details:', {
        editedSOP,
        selectedSOP,
        isNewTemplate,
        error: error.message
      });
    }
  };

  const handleCancel = () => {
    if (isNewTemplate) {
      // For new templates, just close the modal and reset state
      setIsModalOpen(false);
      setIsEditing(false);
      setIsNewTemplate(false);
      setIsAttachmentOnly(false);
      setNewTemplateAttachmentFile(null);
      setSelectedSOP(null);
      setEditedSOP(null);
    } else if (selectedSOP) {
      // For existing SOPs, reset to original values
      setEditedSOP({ ...selectedSOP });
      setIsEditing(false);
    } else {
      setEditedSOP(null);
      setIsEditing(false);
      setIsModalOpen(false);
      setIsNewTemplate(false);
    }
  };




  const handleBack = () => window.history.back();


  return (
    <div className="min-h-screen p-6 relative z-0">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
          <div className="flex items-center justify-between mb-8">
            <div className="flex-1 flex items-center">
              <BackButton onClick={handleBack} />
            </div>
            <div className="flex items-center">
              <FaBook className="text-4xl text-red-600 mr-3" />
              <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>SOP Templates</h1>
            </div>
            <div className="flex-1 flex justify-end">
              {activeTab !== 'contact-to-build' && activeTab !== 'html-template' && <AddButton onClick={handleNewTemplate} />}
            </div>
          </div>

          {/* Tabs */}
          <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} mb-8`}>
            <nav className="-mb-px flex justify-center space-x-8" aria-label="Tabs">
              {tabs.map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <div key={tab.id} className={`inline-flex items-center border-b-2 ${active ? 'border-red-500' : 'border-transparent'}`}>
                    <button
                      onClick={() => setActiveTab(tab.id)}
                      className={`whitespace-nowrap py-4 px-1 font-medium text-sm flex items-center ${active ? 'text-red-600' : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <span className="mr-2 text-lg leading-none flex items-center">
                        {tab.staticEmoji || groupEmojis[tab.id] || '📁'}
                      </span>
                      {tab.label}
                    </button>
                    {active && !tab.staticEmoji && (
                      <button
                        type="button"
                        onClick={() => { setEmojiPickerMode('group'); setShowEmojiPicker(true); }}
                        title="Change the emoji shown on the portal section header for this group"
                        className={`ml-1 p-1 rounded ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                      >
                        <FaPencilAlt className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>

          {/* Contact to Build / HTML Template tabs: master-template editors. Other tabs: SOP cards. */}
          {activeTab === 'contact-to-build' ? (
            <ContactToBuildTemplateEditor darkMode={darkMode} />
          ) : activeTab === 'html-template' ? (
            <SopHtmlTemplateEditor darkMode={darkMode} />
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(sops[activeTab] || []).map((sop, index) => (
              <div
                key={sop.id || index}
                className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer relative`}
                onClick={() => handleSOPClick(sop)}
              >
                {/* Reorder controls — swap position within the department and save */}
                <div className="absolute top-2 right-2 flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); moveSOP(index, -1); }}
                    disabled={index === 0}
                    title="Move up"
                    className={`p-1 rounded ${index === 0 ? 'opacity-30 cursor-not-allowed' : darkMode ? 'text-gray-300 hover:bg-gray-500' : 'text-gray-500 hover:bg-gray-200'}`}
                  >
                    <FaArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); moveSOP(index, 1); }}
                    disabled={index === (sops[activeTab] || []).length - 1}
                    title="Move down"
                    className={`p-1 rounded ${index === (sops[activeTab] || []).length - 1 ? 'opacity-30 cursor-not-allowed' : darkMode ? 'text-gray-300 hover:bg-gray-500' : 'text-gray-500 hover:bg-gray-200'}`}
                  >
                    <FaArrowDown className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex flex-col items-center mb-4">
                  {sop.emoji && <span className="text-3xl leading-none mb-2">{sop.emoji}</span>}
                  <h3 className={`text-lg font-semibold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>{sop.title || 'Untitled'}</h3>
                  <span className={`mt-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${darkMode ? 'bg-gray-800 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                    v{sop.version || '1.0'}
                  </span>
                  {sop.description && (
                    <p className={`text-xs mt-1 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{sop.description}</p>
                  )}
                </div>
                <button className="w-full bg-red-600 text-white py-1.5 px-3 rounded-md hover:bg-red-700 transition-colors duration-200 text-sm">
                  View Details
                </button>
              </div>
            ))}
          </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedSOP && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1001]">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col relative`}>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              title="Close"
              aria-label="Close"
              className={`absolute top-3 left-3 z-10 p-1.5 rounded-full transition ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex-1 overflow-y-auto thin-scrollbar px-8 pt-12 pb-4">
              <div className="flex flex-col items-center mb-8">
                {isEditing ? (
                  <input
                    type="text"
                    value={editedSOP?.title || ''}
                    onChange={(e) => setEditedSOP({ ...(editedSOP || {}), title: e.target.value })}
                    className={`text-3xl font-bold mb-2 text-center border rounded px-2 py-1 ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                  />
                ) : (
                  <h2 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedSOP.title}</h2>
                )}
              </div>

                             <div className="space-y-6">
                 {(isNewTemplate || isEditing) && (
                   <div className={`bg-gray-50 dark:bg-gray-800/60 rounded-xl p-6 border border-gray-200 dark:border-gray-800`}>
                     <div className="grid grid-cols-3 gap-4">
                       {/* Department */}
                       <div className="flex flex-col items-center text-center">
                         <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Department</h3>
                         <select
                           value={editedSOP?.department || 'design'}
                           onChange={(e) => setEditedSOP({ ...(editedSOP || {}), department: e.target.value })}
                           className={`w-full border rounded px-2 py-2 text-sm text-center ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300'}`}
                         >
                           <option value="design">Design</option>
                           <option value="construction">Construction</option>
                           <option value="office">Office</option>
                         </select>
                       </div>
                       {/* Card Emoji */}
                       <div className="flex flex-col items-center text-center">
                         <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Card Emoji</h3>
                         <button
                           type="button"
                           onClick={() => { setEmojiPickerMode('card'); setShowEmojiPicker(true); }}
                           className={`w-16 h-16 flex items-center justify-center rounded-xl border text-3xl transition-colors bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700`}
                           title="Choose an emoji"
                         >
                           {editedSOP?.emoji ? (
                             <span className="leading-none">{editedSOP.emoji}</span>
                           ) : (
                             <FaPlus className={`text-base ${darkMode ? 'text-gray-300' : 'text-gray-400'}`} />
                           )}
                         </button>
                       </div>
                       {/* Version — click to bump */}
                       <div className="flex flex-col items-center text-center">
                         <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Version</h3>
                         <button
                           type="button"
                           onClick={bumpVersion}
                           title="Click to update to the next version"
                           className={`inline-flex items-center gap-1.5 px-4 h-16 rounded-lg border text-lg font-semibold transition-colors ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100 hover:bg-gray-500' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                         >
                           v{editedSOP?.version || '1.0'}
                           <FaArrowUp className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                         </button>
                         <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                           Click to update.
                         </p>
                       </div>
                     </div>
                     <h3 className={`text-lg font-semibold mt-4 mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Description</h3>
                     <textarea
                       value={editedSOP?.description || ''}
                       onChange={(e) => setEditedSOP({ ...(editedSOP || {}), description: e.target.value })}
                       rows={3}
                       placeholder="Short description shown on the SOP card…"
                       className={`w-full resize-y border rounded px-3 py-2 text-sm ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                     />
                   </div>
                 )}
                {/* Existing attachment-only template: show attachment with View/Download */}
                {((isEditing ? editedSOP : selectedSOP)?.attachmentOnly && (isEditing ? editedSOP : selectedSOP)?.attachment) ? (
                  <div className={`bg-gray-50 dark:bg-gray-800/60 rounded-xl p-6 border border-gray-200 dark:border-gray-800`}>
                    <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Attachment</h3>
                    <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {(isEditing ? editedSOP : selectedSOP).attachment.name}
                    </p>
                    <div className="flex gap-3">
                      <a
                        href={(isEditing ? editedSOP : selectedSOP).attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        View
                      </a>
                      <a
                        href={(isEditing ? editedSOP : selectedSOP).attachment.url}
                        download={(isEditing ? editedSOP : selectedSOP).attachment.name}
                        className="px-4 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      >
                        Download
                      </a>
                    </div>
                  </div>
                ) : isNewTemplate && isEditing && isAttachmentOnly ? (
                  /* New template, Attachment Only: name, department, attachment upload */
                  <div className={`bg-gray-50 dark:bg-gray-800/60 rounded-xl p-6 border border-gray-200 dark:border-gray-800`}>
                    <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Attachment</h3>
                    <input
                      type="file"
                      onChange={(e) => setNewTemplateAttachmentFile(e.target.files?.[0] || null)}
                      className={`w-full text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                      accept="*"
                    />
                    {newTemplateAttachmentFile && (
                      <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Selected: {newTemplateAttachmentFile.name}
                      </p>
                    )}
                  </div>
                ) : isEditing ? (
                  <SopRichEditor
                    mode="full"
                    value={editedSOP?.contentJSON}
                    onChange={(json) => setEditedSOP((prev) => ({ ...(prev || {}), contentJSON: json }))}
                    darkMode={darkMode}
                    toolbarSlot={toolbarSlot}
                  />
                ) : (
                  <SopRichView
                    value={selectedSOP.contentJSON || migrateSopRecord(selectedSOP)}
                    darkMode={darkMode}
                  />
                )}
              </div>
            </div>

              {/* Action Buttons — always visible */}
              <div className={`shrink-0 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} px-8 py-4 flex justify-between items-center`}>
                <div className="flex items-center gap-4">
                  {isEditing && <div ref={setToolbarSlot} />}
                  {isNewTemplate && isEditing && (
                    <button
                      type="button"
                      onClick={() => setIsAttachmentOnly(prev => !prev)}
                      className={`px-4 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${isAttachmentOnly ? 'bg-red-600 text-white border-red-600' : darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                    >
                      Attachment Only
                    </button>
                  )}
                  {isEditing && !isNewTemplate && (
                    <button
                      onClick={handleDeleteSOP}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Delete
                    </button>
                  )}
                  {/* Attachment-only templates hide the Edit button, so their Delete
                      lives in view mode (otherwise there'd be no way to remove them). */}
                  {!isEditing && !isNewTemplate && selectedSOP?.attachmentOnly && selectedSOP?.attachment && (
                    <button
                      onClick={handleDeleteSOP}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Delete
                    </button>
                  )}
                </div>
                <div className="flex space-x-4">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleCancel}
                        className={`px-4 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                      >
                        Cancel
                      </button>
                      <SaveButton
                        onClick={handleSave}
                        variant="green"
                        disabled={isNewTemplate && isAttachmentOnly && !newTemplateAttachmentFile}
                        title={isNewTemplate && isAttachmentOnly && !newTemplateAttachmentFile ? 'Select an attachment first' : ''}
                      />
                    </>
                  ) : (
                    <>
                      {selectedSOP?.attachmentOnly && selectedSOP?.attachment ? (
                        <a
                          href={selectedSOP.attachment.url}
                          download={selectedSOP.attachment.name}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Download
                        </a>
                      ) : (
                        <EditButton label="Edit" onClick={handleEdit} />
                      )}
                    </>
                  )}
                </div>
              </div>
          </div>
        </div>
      )}

      {/* Full emoji picker (same as Sales / HR material templates) */}
      {showEmojiPicker && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1100]"
          onClick={() => setShowEmojiPicker(false)}
        >
          <div
            className={`bg-white dark:bg-gray-900 rounded-2xl p-4 w-full max-w-sm`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className={`text-lg font-semibold mb-3 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Choose an icon</h3>
            <div className="flex justify-center">
              <React.Suspense
                fallback={
                  <div className="flex items-center justify-center h-72 w-full">
                    <BBALoader size={32} />
                  </div>
                }
              >
                <EmojiPicker
                  onEmojiClick={async (emojiData) => {
                    if (emojiPickerMode === 'group') {
                      const updated = await saveGroupEmoji('sop', activeTab, emojiData.emoji, groupEmojis);
                      setGroupEmojis(updated);
                    } else {
                      setEditedSOP((prev) => ({ ...(prev || {}), emoji: emojiData.emoji }));
                    }
                    setShowEmojiPicker(false);
                  }}
                  theme={darkMode ? 'dark' : 'light'}
                  width="100%"
                  height={380}
                  lazyLoadEmojis
                  previewConfig={{ showPreview: false }}
                />
              </React.Suspense>
            </div>
            <div className="flex items-center justify-between mt-3">
              <button
                type="button"
                onClick={async () => {
                  if (emojiPickerMode === 'group') {
                    const updated = await saveGroupEmoji('sop', activeTab, '', groupEmojis);
                    setGroupEmojis(updated);
                  } else {
                    setEditedSOP((prev) => ({ ...(prev || {}), emoji: '' }));
                  }
                  setShowEmojiPicker(false);
                }}
                className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
              >
                {emojiPickerMode === 'group' ? 'Use default' : 'Clear emoji'}
              </button>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(false)}
                className={`px-3 py-2 text-sm font-medium border rounded-md ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOPTemplates;